import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button, Space, Tag, message } from "antd";
// FIXED IMPORTS for src/core/modals/subcriptions/
import Table from "../../pagination/datatable";
import {
  fetchLicenses,
  addLicense,
  editLicense,
  removeLicense,
} from "../../redux/license.slice";
import LicenseFormModal from "./LicenseFormModal"; // Local import
import StickyBar from "../../common/StickyBar";
import CommonFooter from "../../common/footer/commonFooter";
import LoadingState from "../../common/LoadingState";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../../../utils/priceUtils"; 

const getLicenseId = (l) => l?.licenseId ?? l?.lisence_id;

const Licenses = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { licenses, loading } = useSelector((state) => state.license);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [licenseToDelete, setLicenseToDelete] = useState(null);

  
  const { company } = useSelector((state) => state.company);
  const currencyObj = company?.currency;
  const currencyCode = currencyObj?.currency_code ?? 'TND';
  const decimalPlaces = company?.currency?.decimal_places ?? 3;

  useEffect(() => {
    dispatch(fetchLicenses());
  }, [dispatch]);

  const handleAdd = () => {
    setIsEditMode(false);
    setSelectedLicense(null);
    setShowModal(true);
  };

  const handleEdit = (license) => {
    setIsEditMode(true);
    setSelectedLicense(license);
    setShowModal(true);
  };

  const handleCancel = () => setShowModal(false);

  const askDelete = (license) => {
    setLicenseToDelete(getLicenseId(license));
    setShowDeleteModal(true);
  };

  const deleteLicense = () => {
    dispatch(removeLicense(licenseToDelete))
      .unwrap()
      .then(() => {
        setShowDeleteModal(false);
        message.success(t("common.deleteSuccess"));
        dispatch(fetchLicenses());
      })
      .catch((err) => {
        message.error(t("common.deleteError"));
        console.error(err);
      });
  };

  const handleFormSubmit = (values) => {
    const action = isEditMode
      ? editLicense({ id: getLicenseId(selectedLicense), license: values })
      : addLicense(values);

    dispatch(action)
      .unwrap()
      .then(() => {
        handleCancel();
        message.success(
          isEditMode ? t("common.updateSuccess") : t("common.createSuccess"),
        );
        dispatch(fetchLicenses());
      })
      .catch((err) => {
        message.error(t("common.saveError"));
        console.error(err);
      });
  };

  const columns = [
  {
    title: t("plans.name"),
    dataIndex: "license_name",
    render: (v) => v || "-",
  },
  {
    title: t("plans.description"),
    dataIndex: "license_description",
    render: (v) => v || "-",
    ellipsis: true,
  },
  {
      title: t("plans.monthlyPrice"),
      dataIndex: "license_monthly_price_include_tax", 
      // Updated to use dynamic formatting
      render: (v) => (v != null ? `${formatPrice(v, decimalPlaces)} ${currencyCode}` : "-"),
    },
    {
      title: t("plans.yearlyPrice"),
      dataIndex: "license_yearly_price_include_tax", 
      // Updated to use dynamic formatting
      render: (v) => (v != null ? `${formatPrice(v, decimalPlaces)} ${currencyCode}` : "-"),
    },
  {
    title: t("plans.maxUsers"),
    dataIndex: "license_max_users_number",
    render: (v) => v ?? "-",
  },
  {
    title: t("plans.maxCustomers"),
    dataIndex: "license_max_customers_number",
    render: (v) => v ?? "-",
  },
  {
    title: t("plans.status"),
    dataIndex: "license_is_active", 
    render: (active) => (
      <Tag color={active ? "success" : "default"}>
        {active ? t("plans.active") : t("plans.inactive")}
      </Tag>
    ),
  },
  {
    title: t("plans.actions"),
    key: "actions",
    render: (_, record) => (
      <div className="action-table-data">
        <div className="edit-delete-action">
          <a className="me-2 p-2" onClick={() => handleEdit(record)}>
            <i data-feather="edit" className="feather-edit"></i>
          </a>
          <a className="confirm-text p-2" onClick={() => askDelete(record)}>
            <i data-feather="trash-2" className="feather-trash-2"></i>
          </a>
        </div>
      </div>
    ),
  },
];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <StickyBar
            title={t("plans.title")}
            count={licenses?.length || 0}
            actions={
              <Space size="middle">
                <Button className="btn-customized-primary" onClick={handleAdd}>
                  <i className="ti ti-circle-plus me-1"></i>
                  {t("plans.addPlan")}
                </Button>
              </Space>
            }
          />
          <div className="card table-list-card">
            <div className="card-body">
              {loading ? (
                <LoadingState
                  type="section"
                  message={t("common.loadingData")}
                />
              ) : (
                <div className="table-responsive">
                  <Table
                    columns={columns}
                    dataSource={licenses || []}
                    rowKey={(r) => getLicenseId(r)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>

      <LicenseFormModal
        open={showModal}
        onCancel={handleCancel}
        onSubmit={handleFormSubmit}
        isEdit={isEditMode}
        initialData={selectedLicense}
      />

      <Modal
        onCancel={() => setShowDeleteModal(false)}
        open={showDeleteModal}
        footer=""
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content p-5 px-3 text-center">
                <span className="rounded-circle d-inline-flex p-2 bg-danger-transparent mb-2">
                  <i className="ti ti-trash fs-24 text-danger" />
                </span>
                <h4 className="fs-20 text-gray-9 fw-bold mb-2 mt-1">
                  {t("plans.deleteTitle")}
                </h4>
                <p className="text-gray-6 mb-0 fs-16">
                  {t("plans.deleteConfirm")}
                </p>
                <div className="modal-footer-btn mt-3 d-flex justify-content-center">
                  <Button
                    size="large"
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    size="large"
                    type="button"
                    onClick={deleteLicense}
                    className="btn btn-submit fs-13 fw-medium p-2 px-3"
                  >
                    {t("common.confirmDelete")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Licenses;
